import { useState } from 'react';
import { useAuth } from '../lib/auth-context';
import { claimAccount, describePasskeyError, type ClaimResult } from '../lib/passkey';

/**
 * Shown only while fewer than two accounts exist. After the second claim the
 * API stops serving this flow entirely, so this component becomes unreachable.
 */
export function Claim({ remaining, onCancel }: { remaining: number; onCancel?: () => void }) {
  const [secret, setSecret] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [deviceName, setDeviceName] = useState(guessDeviceName());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ClaimResult | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setResult(await claimAccount(secret, displayName.trim(), deviceName.trim() || 'Phone'));
    } catch (err) {
      setError(describePasskeyError(err));
    } finally {
      setBusy(false);
    }
  }

  if (result) return <ClaimSuccess result={result} />;

  return (
    <main className="center">
      <form className="card" onSubmit={submit}>
        <h1>Set up your account</h1>
        <p className="hint">
          {remaining === 2
            ? 'Two accounts can be created. This screen disappears permanently once both exist.'
            : 'One account left. This screen disappears permanently once it is created.'}
        </p>

        <label>
          Setup code
          <input
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            placeholder="From the server .env"
            required
          />
        </label>

        <label>
          Your name
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoComplete="given-name"
            maxLength={40}
            required
          />
        </label>

        <label>
          Name this device
          <input
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
            maxLength={60}
            required
          />
        </label>

        {error && <p className="error">{error}</p>}

        <button type="submit" className="primary big" disabled={busy || !secret || !displayName}>
          {busy ? 'Waiting for your device…' : 'Create passkey'}
        </button>

        {onCancel && (
          <p className="hint setup-note">
            Already have an account?{' '}
            <button type="button" className="link" onClick={onCancel}>
              Sign in instead
            </button>
          </p>
        )}
      </form>
    </main>
  );
}

function ClaimSuccess({ result }: { result: ClaimResult }) {
  const { refresh } = useAuth();
  const [saved, setSaved] = useState(false);

  return (
    <main className="center">
      <div className="card">
        <h1>You're set up, {result.user.displayName}</h1>

        <section>
          <h2>Save your recovery codes</h2>
          <p className="hint">
            These are shown once and never again. Each works a single time, and they are the way
            back in if you lose your phone. Put them in a password manager or print them.
          </p>
          <ul className="codes">
            {result.recoveryCodes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <button
            className="secondary"
            onClick={() => {
              void navigator.clipboard.writeText(result.recoveryCodes.join('\n'));
            }}
          >
            Copy codes
          </button>
        </section>

        <section>
          <h2>Add a backup code generator</h2>
          <p className="hint">
            Optional but recommended. Open this in your authenticator app so you can sign in
            without your passkey.
          </p>
          <a className="secondary block" href={result.totpUri}>
            Open in authenticator app
          </a>
          <details>
            <summary>Show setup key instead</summary>
            <code className="wrap">{result.totpUri}</code>
          </details>
        </section>

        <label className="check">
          <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
          I have saved my recovery codes
        </label>

        <button className="primary big" disabled={!saved} onClick={() => void refresh()}>
          Continue
        </button>
      </div>
    </main>
  );
}

function guessDeviceName(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android phone';
  if (/Mac/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows PC';
  return 'My device';
}
